/**
 * The real Operations `WorkspaceComponentRegistry` (ADR-UI-003 Round B):
 * wires the 7 existing, already-tested Operations panels into the FlexLayout
 * engine built in Round A. Each `lazyComponent` is a module-level-stable
 * `React.lazy(() => import(...))` reference to that panel's thin adapter
 * (`<feature>-tab.tsx`), never the panel view itself directly — the adapter
 * is what pulls real store/capability values out of
 * `OperationsWorkspaceRuntimeContext` (see `operations-workspace-runtime.tsx`).
 *
 * Ids match `WORKSPACE_COMPONENT_IDS` (`apps/web/src/workspace/types.ts`)
 * exactly — the same ids already used throughout the pre-cutover
 * `operations-route.tsx`/panel test suites, reused verbatim, not renamed.
 * `resource-requests` (the 8th registered id) is intentionally NOT defined
 * here: it has no live default-preset placement yet (M10 read-permission
 * gap), matching `FLEXLAYOUT_WORKSPACE_ARCHITECTURE.md` §2's documented
 * scope — never a speculative panel for a module with no backend/UI.
 *
 * `requiredPermissions` are the REAL read-permission codes each panel
 * already gated on in the pre-cutover route (confirmed against
 * `apps/web/src/app/generated/shell-permission-codes.ts`, the generated
 * mirror of the backend's `PERMISSION_CODES`):
 *
 * - `mission-board`: `mission_read` (also the route's own gross
 *   `canViewOperations` gate; declared here too for defence in depth).
 * - `personnel-directory` / `asset-directory` / `readiness-status`:
 *   `person_read` / `asset_read` / `readiness_read`.
 * - `command-structure`: `command_structure_read` (the panel BODY
 *   permission; propose/review/apply are separate sub-capabilities gated
 *   inside the view itself, unchanged).
 * - `situation-map`: `[]` — the embedded Operations panel remains available
 *   after the route-level `mission_read` gate because this registry currently
 *   supports only "all permissions" matching, while map access is an ANY of
 *   `map_object_read/create/review/publish`. The standalone protected feature
 *   uses that ANY policy; the backend independently authorizes every map read.
 * - `etb-log`: `[]` — by the same evidence standard: no dedicated ETB read
 *   code exists in `PERMISSION_CODES` (only `etb_entry_append`/
 *   `etb_entry_correct`, which gate the append/correct actions, unchanged
 *   inside `EtbLogView`). The pre-cutover route never gated the whole ETB
 *   panel on a read permission either — it was always registered once
 *   `canViewOperations` (`mission_read`) held.
 *
 * `singleton: true` for all 7: each is a unique, non-repeatable panel today
 * (no multi-instance/per-entity variant exists yet).
 */
import { createElement, lazy } from "react";
import { createWorkspaceComponentRegistry } from "../../workspace";
import type {
  WorkspaceComponentDefinition,
  WorkspaceComponentId,
  WorkspaceComponentRegistry,
} from "../../workspace";

const situationMapTab = lazy(() => import("../situation-map/situation-map-tab"));
const mapLegendTab = lazy(() => import("../situation-map/map-legend-tab"));
const situationObjectsTab = lazy(() => import("../situation-map/situation-objects-tab"));
const situationObjectEditorTab = lazy(() => import("../situation-map/situation-object-editor-tab"));
const missionBoardTab = lazy(() => import("../mission-board/mission-board-tab"));
const missionOrdersTab = lazy(() => import("../mission-orders/mission-orders-tab"));
// `MissionWorkbenchTab` and `CollaborationTab` take optional presentation
// props for their non-registry mounts. The registry renders them
// parameterless, so wrap each in a zero-parameter adapter that stays
// structurally assignable to `ComponentType<WorkspaceTabProps>` (the same
// contract mission-board-tab.tsx documents for prop-less adapters).
const missionWorkbenchTab = lazy(async () => {
  const module = await import("../mission-workbench/mission-workbench-tab");
  return { default: () => createElement(module.default) };
});
const fieldTasksTab = lazy(() => import("../field-tasks/field-tasks-tab"));
const mobilizationTab = lazy(() => import("../mobilization/mobilization-tab"));
const orderExecutionStatusPanel = lazy(() => import("../mobilization/order-execution-status-panel"));
const deploymentAssetStatusPanel = lazy(() => import("../mobilization/deployment-asset-status-panel"));
const etbLogTab = lazy(() => import("../etb-log/etb-log-tab"));
const collaborationTab = lazy(async () => {
  const module = await import("../collaboration/collaboration-tab");
  return { default: () => createElement(module.default) };
});
const personnelDirectoryTab = lazy(() => import("../personnel-directory/personnel-directory-tab"));
const assetDirectoryTab = lazy(() => import("../asset-directory/asset-directory-tab"));
const readinessStatusTab = lazy(() => import("../readiness-status/readiness-status-tab"));
const commandStructureTab = lazy(() => import("../command-structure/command-structure-tab"));
const capabilityWorkbenchTab = lazy(() => import("../capability-workbench/capability-workbench-tab"));
const capabilityRequirementsPanelTab = lazy(async () => {
  const module = await import("../capability-workbench/capability-workspace-panel-tab");
  return { default: module.CapabilityRequirementsPanelTab };
});
const capabilityCoveragePanelTab = lazy(async () => {
  const module = await import("../capability-workbench/capability-workspace-panel-tab");
  return { default: module.CapabilityCoveragePanelTab };
});
const capabilityCandidatesPanelTab = lazy(async () => {
  const module = await import("../capability-workbench/capability-workspace-panel-tab");
  return { default: module.CapabilityCandidatesPanelTab };
});
const capabilitySelectedUnitPanelTab = lazy(async () => {
  const module = await import("../capability-workbench/capability-workspace-panel-tab");
  return { default: module.CapabilitySelectedUnitPanelTab };
});
const capabilityTeamPanelTab = lazy(async () => {
  const module = await import("../capability-workbench/capability-workspace-panel-tab");
  return { default: module.CapabilityTeamPanelTab };
});
const capabilityCommitmentTab = lazy(() => import("../capability-workbench/capability-commitment-tab"));
const deploymentRosterPanel = lazy(() => import("../mobilization/deployment-roster-panel"));
const resourceRequestsTab = lazy(() => import("../resource-requests/resource-requests-tab"));
const logisticsTab = lazy(() => import("../logistics/logistics-tab"));
const incidentReportingTab = lazy(() => import("../forms/incident-reporting-tab"));
const requirementReviewTab = lazy(() => import("../requirement-review/requirement-review-tab"));
const safetyChecksTab = lazy(() => import("../safety-checks/safety-checks-tab"));
const lageTab = lazy(() => import("../lage/lage-tab"));
const reportingTab = lazy(() => import("../reporting/reporting-tab"));
const alertingTab = lazy(() => import("../alerting/alerting-tab"));
const dispatchAlertingTab = lazy(async () => {
  const module = await import("../alerting/alerting-tab");
  return { default: module.DispatchAlertingTab };
});
const alertResponsesTab = lazy(async () => {
  const module = await import("../alerting/alerting-tab");
  return { default: module.AlertResponsesTab };
});
const activeMissionsRail = lazy(() => import("./active-missions-rail"));
const incidentContextTab = lazy(() => import("./incident-context-tab"));
const regionalCoordinationTab = lazy(() => import("./regional-coordination-tab"));
const regionalSupportInboxTab = lazy(() => import("./regional-support-inbox-tab"));
const workflowBoardTab = lazy(() => import("../workflow-board/workflow-board-tab"));
const incidentCompletionTab = lazy(() => import("../incident-completion/incident-completion-tab"));

export const operationsComponentDefinitions: readonly WorkspaceComponentDefinition[] = [
  {
    allowedContexts: ["global", "mission"],
    id: "feature-active-missions",
    lazyComponent: activeMissionsRail,
    minimumHeight: 180,
    minimumWidth: 180,
    requiredPermissions: ["mission_read"],
    singleton: true,
    title: { de: "Aktive Einsätze", en: "Active incidents" },
    titleKey: "incidentsNav",
  },
  {
    allowedContexts: ["global", "mission"],
    id: "feature-incident-context",
    // Deliberately NOT `requiresIncident`: this panel *is* the incident
    // switcher. Gating it replaced the only control that can select an
    // incident with a notice telling the operator to select one, and the rail
    // it pointed at lists open incidents only — so with every incident closed
    // there was no way back into a context at all.
    lazyComponent: incidentContextTab,
    minimumHeight: 240,
    minimumWidth: 220,
    requiredPermissions: ["mission_read"],
    singleton: true,
    title: { de: "Einsatz", en: "Incident" },
    titleKey: "incidentsNav",
  },
  {
    allowedContexts: ["global", "mission"],
    id: "feature-regional-coordination",
    requiresIncident: true,
    lazyComponent: regionalCoordinationTab,
    minimumHeight: 200,
    minimumWidth: 220,
    requiredPermissions: ["mission_read"],
    singleton: true,
    title: { de: "Koordination", en: "Coordination" },
    titleKey: "incidentsNav",
  },
  {
    allowedContexts: ["global", "mission"],
    id: "feature-regional-support-inbox",
    lazyComponent: regionalSupportInboxTab,
    minimumHeight: 240,
    minimumWidth: 300,
    requiredPermissions: ["mission_read"],
    singleton: true,
    title: { de: "LuK-Unterstützungseingang", en: "LuK support inbox" },
    titleKey: "incidentsNav",
  },
  {
    allowedContexts: ["global", "mission"],
    // The sequential E3 operating picture — gated only on the gross
    // operations-read permission so EVERY function sees the shared flow;
    // the actions inside deep-link into panels that carry their own gates.
    id: "workflow-board",
    requiresIncident: true,
    lazyComponent: workflowBoardTab,
    minimumHeight: 360,
    minimumWidth: 380,
    requiredPermissions: ["mission_read"],
    singleton: true,
    titleKey: "workflowBoardPanelTitle",
  },
  {
    allowedContexts: ["global", "mission"],
    /*
     * Einsatzbeendigung und Einsatzabschluss — phase 6/7 of the E3 sequence.
     * Gated on `mission_read` only: an incident commander must be able to SEE
     * what is holding the incident open even when the outstanding task belongs
     * to another function. The ending decision itself needs `mission_close`,
     * which the server-calculated plan reports per path.
     */
    id: "incident-completion",
    requiresIncident: true,
    lazyComponent: incidentCompletionTab,
    minimumHeight: 320,
    minimumWidth: 360,
    requiredPermissions: ["mission_read"],
    singleton: true,
    title: { de: "Beendigung", en: "Completion" },
    titleKey: "incidentsNav",
  },
  {
    allowedContexts: ["global", "mission"],
    id: "situation-map",
    requiresIncident: true,
    lazyComponent: situationMapTab,
    minimumHeight: 360,
    minimumWidth: 480,
    requiredPermissions: [],
    singleton: true,
    titleKey: "situationMapPanelTitle",
  },
  {
    allowedContexts: ["global", "mission"],
    id: "situation-objects",
    requiresIncident: true,
    lazyComponent: situationObjectsTab,
    minimumHeight: 220,
    minimumWidth: 320,
    // Same reasoning as `situation-map`: M14 exposes no `map_*_read`
    // permission code to gate discovery on, and every row action is already
    // gated on the caller's own write capabilities plus the row's state.
    requiredPermissions: [],
    singleton: true,
    title: { de: "Kartenobjekte", en: "Map objects" },
    titleKey: "situationMapPanelTitle",
  },
  {
    allowedContexts: ["global", "mission"],
    id: "situation-object-editor",
    requiresIncident: true,
    lazyComponent: situationObjectEditorTab,
    minimumHeight: 240,
    minimumWidth: 320,
    requiredPermissions: [],
    singleton: true,
    title: { de: "Objekt erfassen", en: "Record object" },
    titleKey: "situationMapPanelTitle",
  },
  {
    allowedContexts: ["global", "mission"],
    id: "map-legend",
    requiresIncident: true,
    lazyComponent: mapLegendTab,
    minimumHeight: 240,
    minimumWidth: 200,
    // Reuse the map's own title key as the required-by-type fallback; the
    // literal `title` below is what actually renders on the tab. No dedicated
    // read-permission code — it only toggles client-side visibility of data
    // the situation map already loaded.
    requiredPermissions: [],
    singleton: true,
    title: { de: "Kartenebenen", en: "Map layers" },
    titleKey: "situationMapLayersLegend",
  },
  {
    allowedContexts: ["global", "mission"],
    id: "mission-board",
    lazyComponent: missionBoardTab,
    minimumHeight: 240,
    minimumWidth: 360,
    requiredPermissions: ["mission_read"],
    singleton: true,
    // Renamed from "Mission selection": selecting an incident is what the left
    // border rail does. What is unique here is registering and closing one.
    title: { de: "Einsatzverwaltung", en: "Incident register" },
    titleKey: "missionBoardPanelTitle",
  },
  // The three functions that used to share `mission-board`. Each carries the
  // permission its own work needs, so a unit leader no longer has to open a
  // command panel to file a declaration.
  {
    allowedContexts: ["mission"],
    // Redesign migration (Slice 1 — Mission and Command): this panel now
    // renders the Level 2 `mission` workbench — ten approved mockup screens
    // as states of one workbench, fed by a typed adapter over the same
    // `MissionOrdersStore`/`CommandStructureStore`/`MissionBoardStore`. Two
    // screens (`emergency-override`, `reopen`) have no backend counterpart
    // and are rendered permanently unavailable rather than simulated — see
    // `mission-workbench-adapter.ts`'s documented conflicts. The legacy
    // `mission-orders-tab` stays registered under `legacy-mission-orders`
    // below as a full-fidelity fallback while the new workbench is verified
    // against every role; see docs/design/redesign-panel-audit.md.
    id: "mission-orders",
    requiresIncident: true,
    lazyComponent: missionWorkbenchTab,
    minimumHeight: 240,
    minimumWidth: 360,
    // `mission_read`, exactly as the former `mission-board` panel required:
    // gating discovery on `order_read` would take the order register away from
    // the LuK dispatcher, who could always see it. What is actionable inside
    // is still governed by `missionOrderCapabilities`.
    requiredPermissions: ["mission_read"],
    singleton: true,
    title: { de: "Einsatzaufträge", en: "Mission orders" },
    titleKey: "missionOrdersPanelTitle",
  },
  {
    allowedContexts: ["mission"],
    // TEMPORARY legacy wrapper — see docs/design/redesign-panel-audit.md.
    // Full-fidelity fallback for the compose/issue/acknowledge/complete flow
    // while the new `mission-orders` (Level 2 workbench) panel is verified
    // against every role. Not a stub: this is the complete pre-redesign
    // `MissionOrdersView`, kept reachable rather than deleted.
    id: "legacy-mission-orders",
    requiresIncident: true,
    lazyComponent: missionOrdersTab,
    minimumHeight: 240,
    minimumWidth: 360,
    requiredPermissions: ["mission_read"],
    singleton: true,
    title: { de: "Einsatzaufträge (Altansicht)", en: "Mission orders (legacy)" },
    titleKey: "missionOrdersPanelTitle",
  },
  {
    allowedContexts: ["mission"],
    id: "field-tasks",
    requiresIncident: true,
    lazyComponent: fieldTasksTab,
    minimumHeight: 240,
    minimumWidth: 320,
    requiredPermissions: ["mission_read"],
    singleton: true,
    title: { de: "Feldaufgaben", en: "Field tasks" },
    titleKey: "fieldTasksPanelTitle",
  },
  {
    allowedContexts: ["mission"],
    id: "mobilization",
    requiresIncident: true,
    lazyComponent: mobilizationTab,
    minimumHeight: 240,
    minimumWidth: 360,
    requiredPermissions: ["mission_read"],
    singleton: true,
    title: { de: "Mobilisierung", en: "Mobilization" },
    titleKey: "mobilizationPanelTitle",
  },
  {
    allowedContexts: ["mission"],
    id: "order-execution-status",
    requiresIncident: true,
    lazyComponent: orderExecutionStatusPanel,
    minimumHeight: 220,
    minimumWidth: 320,
    requiredPermissions: ["mission_read"],
    singleton: true,
    title: { de: "Ausführungsstatus", en: "Execution status" },
    titleKey: "missionOrdersPanelTitle",
  },
  {
    allowedContexts: ["mission"],
    id: "deployment-asset-status",
    requiresIncident: true,
    lazyComponent: deploymentAssetStatusPanel,
    minimumHeight: 220,
    minimumWidth: 360,
    requiredPermissions: ["mission_read"],
    singleton: true,
    title: { de: "Verlastungsstatus", en: "Deployment asset status" },
    titleKey: "logisticsPanelTitle",
  },
  {
    allowedContexts: ["mission"],
    id: "etb-log",
    requiresIncident: true,
    lazyComponent: etbLogTab,
    minimumHeight: 240,
    minimumWidth: 320,
    requiredPermissions: [],
    singleton: true,
    titleKey: "etbLogPanelTitle",
  },
  {
    allowedContexts: ["mission"],
    id: "collaboration",
    requiresIncident: true,
    lazyComponent: collaborationTab,
    minimumHeight: 320,
    minimumWidth: 360,
    requiredPermissions: ["channel_read"],
    singleton: true,
    title: { de: "Einsatzchat", en: "Operational chat" },
    titleKey: "workspaceTabMessages",
  },
  {
    allowedContexts: ["global", "resource"],
    id: "personnel-directory",
    lazyComponent: personnelDirectoryTab,
    minimumHeight: 240,
    minimumWidth: 320,
    requiredPermissions: ["person_read"],
    singleton: true,
    titleKey: "personnelPanelTitle",
  },
  {
    allowedContexts: ["global", "resource"],
    id: "asset-directory",
    lazyComponent: assetDirectoryTab,
    minimumHeight: 240,
    minimumWidth: 320,
    requiredPermissions: ["asset_read"],
    singleton: true,
    titleKey: "assetPanelTitle",
  },
  {
    allowedContexts: ["global", "resource"],
    id: "readiness-status",
    lazyComponent: readinessStatusTab,
    minimumHeight: 240,
    minimumWidth: 320,
    requiredPermissions: ["readiness_read"],
    singleton: true,
    titleKey: "readinessStatusPanelTitle",
  },
  {
    allowedContexts: ["mission"],
    id: "command-structure",
    requiresIncident: true,
    lazyComponent: commandStructureTab,
    minimumHeight: 280,
    minimumWidth: 360,
    requiredPermissions: ["command_structure_read"],
    singleton: true,
    titleKey: "commandStructurePanelTitle",
  },
  {
    allowedContexts: ["mission"],
    // Redesign migration: this panel now renders the Level 2 `capability`
    // workbench — nine approved mockup screens as states of one workbench,
    // written against the mockup's composition and fed by a typed adapter over
    // the same `DeploymentPlanningStore`/`RequirementReviewStore`. StAN target
    // and actual-strength evidence, candidate creation and deployment state
    // transitions and requirement creation are ported into this one panel.
    id: "deployment-planning",
    requiresIncident: true,
    lazyComponent: capabilityWorkbenchTab,
    minimumHeight: 360,
    minimumWidth: 520,
    requiredPermissions: ["mission_read"],
    singleton: true,
    // Hosts requirement review AND force sourcing — E3 1.6 + 1.6b, the two
    // stages that used to be indistinguishable adjacent tabs.
    title: { de: "Bedarf & Kräfte", en: "Requirements & forces" },
    titleKey: "deploymentPlanningPanelTitle",
  },
  {
    allowedContexts: ["mission"],
    id: "feature-capability-requirements",
    requiresIncident: true,
    lazyComponent: capabilityRequirementsPanelTab,
    minimumHeight: 160,
    minimumWidth: 260,
    requiredPermissions: ["mission_read"],
    singleton: true,
    title: { de: "Freigegebene Bedarfe", en: "Approved requirements" },
    titleKey: "deploymentPlanningPanelTitle",
  },
  {
    allowedContexts: ["mission"],
    id: "feature-capability-coverage",
    requiresIncident: true,
    lazyComponent: capabilityCoveragePanelTab,
    minimumHeight: 220,
    minimumWidth: 260,
    requiredPermissions: ["mission_read"],
    singleton: true,
    title: { de: "Bedarfsdeckung", en: "Requirement coverage" },
    titleKey: "deploymentPlanningPanelTitle",
  },
  {
    allowedContexts: ["mission"],
    id: "feature-capability-candidates",
    requiresIncident: true,
    lazyComponent: capabilityCandidatesPanelTab,
    minimumHeight: 260,
    minimumWidth: 380,
    requiredPermissions: ["mission_read"],
    singleton: true,
    title: { de: "Passende Einheiten", en: "Compatible units" },
    titleKey: "deploymentPlanningPanelTitle",
  },
  {
    allowedContexts: ["mission"],
    id: "feature-capability-selected-unit",
    requiresIncident: true,
    lazyComponent: capabilitySelectedUnitPanelTab,
    minimumHeight: 360,
    minimumWidth: 360,
    requiredPermissions: ["mission_read"],
    singleton: true,
    title: { de: "Ausgewählte Einheit", en: "Selected unit assessment" },
    titleKey: "deploymentPlanningPanelTitle",
  },
  {
    allowedContexts: ["mission"],
    id: "feature-capability-team",
    requiresIncident: true,
    lazyComponent: capabilityTeamPanelTab,
    minimumHeight: 220,
    minimumWidth: 380,
    requiredPermissions: ["mission_read"],
    singleton: true,
    title: { de: "Team- und Kräfteplanung", en: "Team and resource finalization" },
    titleKey: "deploymentPlanningPanelTitle",
  },
  {
    allowedContexts: ["mission"],
    id: "commitment-allocation",
    requiresIncident: true,
    lazyComponent: capabilityCommitmentTab,
    minimumHeight: 360,
    minimumWidth: 520,
    requiredPermissions: ["mission_read"],
    singleton: true,
    title: { de: "Zusage & Zuordnung", en: "Commitment & allocation" },
    titleKey: "commitmentAllocationPanelTitle",
  },
  {
    allowedContexts: ["mission"],
    id: "deployment-roster",
    requiresIncident: true,
    lazyComponent: deploymentRosterPanel,
    minimumHeight: 360,
    minimumWidth: 440,
    requiredPermissions: ["mission_read"],
    singleton: true,
    title: { de: "Einsatzbesetzung", en: "Deployment roster" },
    titleKey: "mobilizationPanelTitle",
  },
  {
    allowedContexts: ["global", "mission"],
    id: "resource-requests",
    requiresIncident: true,
    lazyComponent: resourceRequestsTab,
    minimumHeight: 360,
    minimumWidth: 520,
    requiredPermissions: ["mission_read"],
    singleton: true,
    title: { de: "Ressourcenanforderungen", en: "Resource requests" },
    titleKey: "workspaceTabResourceRequests",
  },
  {
    allowedContexts: ["mission"],
    id: "logistics",
    requiresIncident: true,
    lazyComponent: logisticsTab,
    minimumHeight: 360,
    minimumWidth: 520,
    requiredPermissions: ["mission_read"],
    singleton: true,
    title: { de: "Logistik & Versorgung", en: "Logistics & supply" },
    titleKey: "logisticsPanelTitle",
  },
  {
    allowedContexts: ["global", "mission"],
    id: "incident-reporting",
    requiresIncident: true,
    lazyComponent: incidentReportingTab,
    minimumHeight: 480,
    minimumWidth: 620,
    requiredPermissions: ["form_read"],
    singleton: true,
    titleKey: "workspaceTabFormsDocuments",
  },
  {
    allowedContexts: ["global", "mission"],
    id: "requirement-review",
    requiresIncident: true,
    lazyComponent: requirementReviewTab,
    minimumHeight: 320,
    minimumWidth: 480,
    requiredPermissions: ["requirement_read"],
    singleton: true,
    // Still registered as its own panel: `?panel=requirement-review` deep links
    // from the workflow board resolve here, and the mobile stacked workspace
    // gives each step its own screen. The dedicated LuK Requirements route uses
    // separate component ids and does not alter this Operations panel.
    title: { de: "Bedarfsprüfung", en: "Requirement review" },
    titleKey: "deploymentPlanningPanelTitle",
  },
  {
    allowedContexts: ["global", "mission"],
    id: "safety-checks",
    requiresIncident: true,
    lazyComponent: safetyChecksTab,
    minimumHeight: 320,
    minimumWidth: 420,
    requiredPermissions: ["recurring_check_read"],
    singleton: true,
    title: { de: "Sicherheitskontrollen", en: "Safety checks" },
    titleKey: "situationMapPanelTitle",
  },
  {
    allowedContexts: ["global", "mission"],
    id: "lage-board",
    requiresIncident: true,
    lazyComponent: lageTab,
    minimumHeight: 320,
    minimumWidth: 460,
    requiredPermissions: ["situation_report_read"],
    singleton: true,
    // "Lage und Meldungen"/"Situation and reports" collided with both the
    // situation map and the closure-report panel. This panel is the running
    // Lagemeldung during the operation; `reporting` is the closing report.
    title: { de: "Lagemeldungen", en: "Situation reports" },
    titleKey: "situationMapPanelTitle",
  },
  {
    allowedContexts: ["global", "mission"],
    id: "reporting",
    requiresIncident: true,
    lazyComponent: reportingTab,
    minimumHeight: 320,
    minimumWidth: 460,
    requiredPermissions: ["report_view"],
    singleton: true,
    title: { de: "Abschlussberichte", en: "Closure reports" },
    titleKey: "situationMapPanelTitle",
  },
  {
    allowedContexts: ["global", "mission"],
    id: "alerting",
    requiresIncident: true,
    lazyComponent: alertingTab,
    minimumHeight: 320,
    minimumWidth: 460,
    requiredPermissions: ["alert_campaign_read"],
    singleton: true,
    title: { de: "Alarmierung", en: "Alerting" },
    titleKey: "situationMapPanelTitle",
  },
  {
    allowedContexts: ["mission"],
    id: "dispatch-alerting",
    requiresIncident: true,
    lazyComponent: dispatchAlertingTab,
    minimumHeight: 280,
    minimumWidth: 360,
    requiredPermissions: ["alert_campaign_read"],
    singleton: true,
    title: { de: "Alarmierung", en: "Alerting" },
    titleKey: "situationMapPanelTitle",
  },
  {
    allowedContexts: ["mission"],
    id: "alert-responses",
    requiresIncident: true,
    lazyComponent: alertResponsesTab,
    minimumHeight: 220,
    minimumWidth: 360,
    requiredPermissions: ["alert_campaign_read"],
    singleton: true,
    title: { de: "Rückmeldungen", en: "Responses" },
    titleKey: "situationMapPanelTitle",
  },
];

export const operationsComponentRegistry: WorkspaceComponentRegistry = createWorkspaceComponentRegistry(
  operationsComponentDefinitions,
);

/**
 * The literal panel title for one locale, or `undefined` when the panel has no
 * literal title and its `titleKey` should be resolved instead.
 *
 * The mobile stacked workspace builds its own panel list rather than reusing
 * the FlexLayout registry, so this keeps its localized titles aligned with the
 * desktop and tablet tabs.
 */
export function operationsPanelTitle(
  id: WorkspaceComponentId,
  locale: "de" | "en",
): string | undefined {
  return operationsComponentRegistry.get(id)?.title?.[locale];
}
